<script lang="ts">
    import Vue from 'vue';
    import { Component } from 'vue-facing-decorator';
    import { insertIntoTextArea1, localToUtcDate } from "@common/Common";
    import { ValidationError } from '@common/types';
    import { DatePicker, DatePickerChangeEvent } from '@progress/kendo-vue-dateinputs';
    import { AttributeEditorDialogViewModel, SaveDateAttributeEventPayload, SaveEmailAttributeEventPayload, SaveMultilineAttributeEventPayload, SaveNumericAttributeEventPayload, SaveTextAttributeEventPayload, TestScriptEventPayload } from '@Quest/CorporateAttributes/Components/AttributeEditorDialog';
    import { AttributeDefinedValueReadModel } from '@Alternatives/Attributes/Domain/readmodels';
    import { RealDefinedValue } from '@Alternatives/Model';   


    @Component({
        components: { 
            DatePicker 
        },
        props: {
            viewModel: { type: Object, required: true }
        },
        watch: {
            testScriptPanelVisible: 'onTestScriptPanelVisibleChanged'
        }
    })
    export default class AttributeEditorDialog extends Vue
    {
        get vm(): AttributeEditorDialogViewModel { return this.$props.viewModel as AttributeEditorDialogViewModel; }

        get opened(): boolean { return this.vm?.opened ?? false; }
        get initialized(): boolean { return this.vm.initialized; }

        get dialogTitle(): string { return this.vm.dialogTitle; }
        get confirmButtonLabel(): string { return this.vm.confirmButtonLabel; }
        get allowChangeAttributeType(): boolean { return this.vm.allowChangeAttributeType; }
        get allowMultipleValuesEnabled(): boolean { return this.vm.allowMultipleValuesEnabled; }
        get allowInputTypeEvaluate(): boolean { return this.vm.allowInputTypeEvaluate; }

        get scriptTestInputs(): { Name: string, Value: number }[] { return this.vm.scriptTestInputs; }
        get scales(): { Id: number, Name: string, Levels: { Name: string, Value: number, Description: string }[] }[] { return this.vm.scales; }
        get numericAttributeNames(): string[] { return this.vm.numericAttributeNames; }

        get scriptTestResult(): string | null { return this.vm.scriptTestResult; }
        set scriptTestResult(val: string | null) { this.vm.scriptTestResult = val; }

        get nameError(): string | null { return this.vm.errors.find(e => e.Context === "Name")?.Error ?? null; }
        get descriptionError(): string | null { return this.vm.errors.find(e => e.Context === "Description")?.Error ?? null; }
        get sectionError(): string | null { return this.vm.errors.find(e => e.Context === "Section")?.Error ?? null; }
        get scriptError(): string | null { return this.vm.errors.find(e => e.Context === "Script")?.Error ?? null; }
        get definedValuesErrors(): string[] { return this.vm.errors.filter(e => e.Context === "DefinedValues").map(e => e.Error); }


        get id(): number { return this.vm.attributeId; }

        get name(): string { return this.vm.name; }
        set name(val: string) { this.vm.name = val; }

        get section(): string | null { return this.vm.section; }
        set section(val: string | null) { this.vm.section = val; }

        get description(): string | null { return this.vm.description; }
        set description(val: string | null) { this.vm.description = val; }

        get script(): string | null { return this.vm.script; }
        set script(val: string | null) { this.vm.script = val; }

        get attributeType(): string { return this.vm.attributeType; }
        set attributeType(val: string) { this.vm.attributeType = val; }

        get inputType(): string { return this.vm.inputType; }
        set inputType(val: string) { this.vm.inputType = val; }

        get isMultivalue(): boolean { return this.vm.isMultivalue; }
        set isMultivalue(val: boolean) { this.vm.isMultivalue = val; }

        get minLength(): number | null { return this.vm.minLength; }
        set minLength(val: number | null) { this.vm.minLength = val; }

        get maxLength(): number | null { return this.vm.maxLength; }
        set maxLength(val: number | null) { this.vm.maxLength = val; }

        get minValue(): number | null { return this.vm.minValue; }
        set minValue(val: number | null) { this.vm.minValue = val; }

        get maxValue(): number | null { return this.vm.maxValue; }
        set maxValue(val: number | null) { this.vm.maxValue = val; }

        get minDate(): Date | null { return this.vm.minDate; }
        set minDate(val: Date | null) { this.vm.minDate = val; }

        get maxDate(): Date | null { return this.vm.maxDate; }
        set maxDate(val: Date | null) { this.vm.maxDate = val; }

        get rows(): number | null { return this.vm.rows; }
        set rows(val: number | null) { this.vm.rows = val; }

        get definedValues(): AttributeDefinedValueReadModel[] { return this.vm.definedValues; }
        set definedValues(val: AttributeDefinedValueReadModel[]) { this.vm.definedValues = val; }

        testScriptPanelVisible: boolean = false;
        
        //watcher: testScriptPanelVisible
        onTestScriptPanelVisibleChanged(val: boolean, oldVal: boolean)
        {
            if (val === true)
            {
                const script = this.script;
                const re = /\[([^\]]*)\]/g;

                const matches: Set<string> = new Set<string>();

                let match;
                while ((match = re.exec(script)) !== null)
                {
                    matches.add(match[1]);
                }

                const newValues = Array.from(matches).map(a =>
                {
                    const vals = this.scriptTestInputs.filter(b => b.Name === a);
                    return { Name: a, Value: vals.length > 0 ? vals[0].Value : 0 };
                });

                this.vm.scriptTestInputs = newValues;
            }
        }
        
        
        addDefinedValue()
        {
            this.definedValues.push({ Name: "", Value: null, Description: null });
        }

        removeDefinedValue(dv: AttributeDefinedValueReadModel)
        {
            this.definedValues.remove(dv);
        }

        insertAttributeIntoScript(attrName: string)
        {
            insertIntoTextArea1(this.$refs.ta as HTMLTextAreaElement, `[${attrName}]`);
            (this.$refs.ta as any).dispatchEvent(new Event('input'));
        }


        onMinDateChanged(event: DatePickerChangeEvent)
        {
            const localDate = event.target.value as Date;
            this.minDate = localToUtcDate(localDate);
        }

        onMaxDateChanged(event: DatePickerChangeEvent)
        {
            const localDate = event.target.value as Date;
            this.maxDate = localToUtcDate(localDate);
        }

        onDefinedLevelDateChanged(event: DatePickerChangeEvent, dv: AttributeDefinedValueReadModel)
        {
            const localDate = event.target.value as Date;
            dv.Value = localToUtcDate(localDate);
        }

        handleLoadScriptTestResult()
        {
            this.scriptTestResult = null;
            this.$emit('test-script', new TestScriptEventPayload(this.script, this.scriptTestInputs));
        }

        handleFormSubmit()
        {
            this.vm.clearValidationErrors();  
            const definedValues = this.definedValues.filter(a => a.Name || a.Value || a.Description);

            switch (this.attributeType)
            {
                case "Real":
                    this.$emit('save-numeric', new SaveNumericAttributeEventPayload(this.id, this.name, this.description, this.section, this.inputType, 
                        this.isMultivalue, this.minValue, this.maxValue, this.script, definedValues as RealDefinedValue[]))
                    break;

                case "Date": {
                    this.$emit('save-date', new SaveDateAttributeEventPayload(this.id, this.name, this.description, this.section, this.inputType,                         
                        this.isMultivalue, this.minDate, this.maxDate, definedValues));
                    break;
                }

                case "Text": {
                    this.$emit('save-text', new SaveTextAttributeEventPayload(this.id, this.name, this.description, this.section, this.inputType,                         
                        this.isMultivalue, this.minLength, this.maxLength, definedValues));
                    break;
                }

                case "Email": {
                    this.$emit('save-email', new SaveEmailAttributeEventPayload(this.id, this.name, this.description, this.section, this.inputType, 
                        this.isMultivalue, definedValues));
                    break;
                }

                case "MultilineText": {
                    this.$emit('save-multiline', new SaveMultilineAttributeEventPayload(this.id, this.name, this.description, this.section, this.rows ?? 3, this.minLength, this.maxLength));
                    break;
                }

                default:
                    console.log(`Unknown attribute type: ${this.attributeType}`);
                    break;
            }
        }

        close()
        {
            this.vm.close();
        }
    }

</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{dialogTitle}}</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading...</h2>

                        <div v-if="initialized" class="container-fluid">
                            <form id="attribute-editor-dialog-form" @submit.prevent="handleFormSubmit">
                                <div class="row">
                                    <div class="col-sm-6">
                                        <div class="form-group">
                                            <label><span class="text-danger">*</span> Name</label>
                                            <input v-model.trim="name" type="text" class="form-control" required minlength="2" maxlength="100" />
                                            <small>Name of the attribute</small>
                                            <div class="error">{{nameError}}</div>
                                        </div>
                                    </div>

                                    <div class="col-sm-6">
                                        <div class="form-group">
                                            <label>Section</label>
                                            <input type="text" v-model.trim="section" class="form-control" maxlength="100" />
                                            <small>Name of the section. Use sections to group attributes e.g. financials.</small>
                                            <div class="error">{{sectionError}}</div>
                                        </div>
                                    </div>

                                    <div class="col-sm-12">
                                        <div class="form-group">
                                            <label>Description</label>
                                            <textarea v-model.trim="description" type="text" class="form-control" rows="3" maxlength="500"></textarea>
                                            <small>Description of the attribute.</small>
                                            <div class="error">{{descriptionError}}</div>
                                        </div>
                                    </div>
                                </div>
                                <hr />

                                <div class="row">
                                    <div class="col-sm-4">
                                        <div class="form-group">
                                            <label>Type</label>
                                            <select class="form-control" v-model="attributeType" :disabled="!allowChangeAttributeType" required>
                                                <option value="Real">Numeric</option>
                                                <option value="Text">Text</option>
                                                <option value="MultilineText">Multiline text</option>
                                                <option value="Date">Date</option>
                                                <option value="Email">Email</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div class="col-sm-4" v-if="attributeType === 'Real'">
                                        <div class="form-group">
                                            <label>Input type</label>
                                            <select class="form-control" v-model="inputType" required>
                                                <option value="Manual">Enter value manually</option>
                                                <option value="List">Pick value from list</option>
                                                <option value="Evaluate" :disabled="allowInputTypeEvaluate === false">
                                                    Based on formula
                                                </option>
                                            </select>
                                        </div>
                                    </div>

                                    <template v-if="attributeType !== 'MultilineText'">
                                        <div class="col-sm-4" v-if="attributeType !== 'Real'">
                                            <div class="form-group">
                                                <label>Input type</label>
                                                <select class="form-control" v-model="inputType">
                                                    <option value="Manual">Enter value manually</option>
                                                    <option value="List">Pick value from list</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div class="col-sm-4" v-if="allowMultipleValuesEnabled === true && inputType !== 'Evaluate'">
                                            <div class="form-group">
                                                <label>Multi-select</label>
                                                <div class="checkbox">
                                                    <label>
                                                        <input v-model="isMultivalue" type="checkbox"> Allow multiple values
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                                <br />

                                <template v-if="inputType === 'Manual'">
                                    <div v-if="attributeType === 'Text'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min length</label>
                                            <input v-model.number="minLength" type="number" class="form-control" min="0" step="1" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max length</label>
                                            <input v-model.number="maxLength" type="number" class="form-control" min="1" step="1" />
                                        </div>
                                    </div>

                                    <div v-if="attributeType === 'MultilineText'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min length</label>
                                            <input v-model.number="minLength" type="number" class="form-control" min="0" step="1" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max length</label>
                                            <input v-model.number="maxLength" type="number" class="form-control" min="1" step="1">
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Number of lines</label>
                                            <input v-model.number="rows" type="number" class="form-control" min="0" step="1" />
                                        </div>
                                    </div>

                                    <div v-if="attributeType === 'Real'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min value</label>
                                            <input v-model.number="minValue" type="number" class="form-control" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max value</label>
                                            <input v-model.number="maxValue" type="number" class="form-control" />
                                        </div>
                                    </div>

                                    <div v-if="attributeType === 'Date'" class="row">
                                        <div class="col-sm-4">
                                            <label>Min value</label>
                                            <DatePicker :value="minDate" @change="onMinDateChanged" />
                                        </div>

                                        <div class="col-sm-4">
                                            <label>Max value</label>
                                            <DatePicker :value="maxDate" @change="onMaxDateChanged" />
                                        </div>
                                    </div>

                                </template>

                                <template v-if="inputType === 'List'">
                                    <div class="mt-3">
                                        <button class="btn btn-sm btn-outline-secondary" @click="addDefinedValue">Add value</button>

                                        <div v-if="attributeType === 'Real'" class="dropdown d-inline">
                                            <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                                Scales templates
                                            </button>

                                            <div class="dropdown-menu" style="max-height: 300px; overflow-y: auto;">
                                                <a v-for="s in scales" :key="s.Id" class="dropdown-item" @click="definedValues = s.Levels">
                                                    {{s.Name}}
                                                </a>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="row mt-3">
                                        <div class="col-sm-3">
                                            <label>Name</label>
                                        </div>
                                        <div class="col-sm-4">
                                            <label>
                                                {{ attributeType !== 'Email' ? 'Value' : 'Email'  }}
                                            </label>
                                        </div>
                                        <div class="col-sm-4">
                                            <label>Description</label>
                                        </div>
                                    </div>

                                    <div v-for="dv in definedValues">
                                        <div class="row" style="margin-top: 5px;">
                                            <div class="col-sm-3">
                                                <input type="text" v-model.trim="dv.Name" class="form-control" required minlength="2" maxlength="100" />
                                            </div>
                                            <div class="col-sm-4">
                                                <div v-if="attributeType === 'Text'">
                                                    <input type="text" v-model.trim="dv.Value" required class="form-control" />
                                                </div>

                                                <div v-if="attributeType === 'Real'">
                                                    <input type="number" v-model.number="dv.Value" required class="form-control" />
                                                </div>

                                                <div v-if="attributeType === 'Date'">
                                                    <DatePicker :value="dv.Value != null ? new Date(dv.Value) : null" @change="onDefinedLevelDateChanged($event, dv)" />
                                                </div>

                                                <div v-if="attributeType === 'Email'">
                                                    <input type="email" v-model.trim="dv.Value" required class="form-control" />
                                                </div>
                                            </div>

                                            <div class="col-sm-4">
                                                <textarea type="text" v-model.trim="dv.Description" class="form-control" rows="2"></textarea>
                                            </div>

                                            <div class="col-sm-1">
                                                <button type="button" @click="removeDefinedValue(dv)" class="btn btn-outline-secondary">
                                                    <i class="fa fa-times-circle"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-for="e in definedValuesErrors">
                                        <div class="error">{{e}}</div>
                                    </div>

                                </template>

                                <template v-if="inputType === 'Evaluate'">
                                    <label>Formula</label>
                                    <div class="d-flex justify-content-end">
                                        <div class="dropdown">
                                            <button class="btn btn-sm btn-secondary dropdown-toggle" data-toggle="dropdown">Insert</button>
                                            <div class="dropdown-menu dropdown-menu-right" style="overflow-y: auto; max-height: 400px;">
                                                <a v-for="attrName in numericAttributeNames" :key="attrName" class="dropdown-item" @click="insertAttributeIntoScript(attrName)">
                                                    {{attrName}}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group">
                                        <textarea ref="ta" v-model="script" rows="5" class="form-control"></textarea>
                                        <div class="error">{{scriptError}}</div>
                                    </div>

                                    <a class="text-primary" @click="testScriptPanelVisible = !testScriptPanelVisible">Test formula</a>
                                    <br />
                                    <div v-if="testScriptPanelVisible === true">
                                        <div class="shadow-none p-3 mb-3 bg-light rounded">
                                            <div v-for="i in scriptTestInputs" class="form-inline">
                                                <label>{{i.Name}}: </label>
                                                <input type="number" v-model.number="i.Value" class="form-control" style="width: 90px;" />
                                            </div>
                                            <br />

                                            <div class="row">
                                                <div class="col-sm-6">
                                                    <button type="button" @click="handleLoadScriptTestResult" class="btn btn-outline-secondary">Test</button>
                                                </div>

                                                <div class="col-sm-6">
                                                    <h3 style="margin: 0;">
                                                        Result: {{scriptTestResult}}
                                                    </h3>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </form>

                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="submit" form="attribute-editor-dialog-form" :disabled="!initialized" class="btn btn-primary">{{confirmButtonLabel}}</button>
                        <button type="button" @click="close" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
    .k-calendar-container {
        z-index: 10000 !important;
    }
</style>